export const CONTROL_EFFECTIVENESS_CATEGORY = "control_effectiveness";
export const CONTROL_EFFECTIVENESS_KEYS = [
  "effective",
  "needs_improvement",
  "ineffective",
  "unrated",
];

export const DEFAULT_CONTROL_EFFECTIVENESS_CONFIG = [
  { value: "effective", label: "Effective", color: "#16A34A", position: 0, status: "active" },
  { value: "needs_improvement", label: "Partially Effective", color: "#D97706", position: 1, status: "active" },
  { value: "ineffective", label: "Ineffective", color: "#DC2626", position: 2, status: "active" },
  { value: "unrated", label: "Not Assessed", color: "#6B7280", position: 3, status: "active" },
];

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
const NORMALIZED_KEY_MAP = {
  effective: "effective",
  "needs improvement": "needs_improvement",
  needs_improvement: "needs_improvement",
  "partially effective": "needs_improvement",
  partially_effective: "needs_improvement",
  ineffective: "ineffective",
  "not effective": "ineffective",
  not_effective: "ineffective",
  unrated: "unrated",
  "not assessed": "unrated",
  not_assessed: "unrated",
};

const normalizeText = (value) => {
  const text = String(value ?? "").trim();
  return text || "";
};

const normalizeLookupKey = (value) =>
  normalizeText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const toTitleCase = (value) =>
  normalizeText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");

const normalizeHexColor = (value, fallback) => {
  const text = normalizeText(value);
  return HEX_COLOR_RE.test(text) ? text.toUpperCase() : fallback;
};

const buildDefaultsMap = () =>
  DEFAULT_CONTROL_EFFECTIVENESS_CONFIG.reduce((acc, item) => {
    acc[item.value] = { ...item };
    return acc;
  }, {});

export const normalizeControlEffectivenessKey = (value, { blankKey = "unrated" } = {}) => {
  const normalized = normalizeLookupKey(value);
  if (!normalized) return blankKey;
  return NORMALIZED_KEY_MAP[normalized] || null;
};

export const mergeControlEffectivenessConfig = (items = []) => {
  const defaultsMap = buildDefaultsMap();
  const next = { ...defaultsMap };

  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = normalizeText(item?.value);
    if (!CONTROL_EFFECTIVENESS_KEYS.includes(key)) return;
    const status = normalizeText(item?.status || "active").toLowerCase();
    const label = normalizeText(item?.label);
    const color = normalizeHexColor(item?.color, "");
    if (!label || !color || status !== "active") return;
    next[key] = {
      ...defaultsMap[key],
      value: key,
      label,
      color,
      status: "active",
    };
  });

  return CONTROL_EFFECTIVENESS_KEYS.map((key) => ({
    ...next[key],
    position: defaultsMap[key].position,
  }));
};

export const getControlEffectivenessMap = (items = []) =>
  mergeControlEffectivenessConfig(items).reduce((acc, item) => {
    acc[item.value] = item;
    return acc;
  }, {});

export const getControlEffectivenessEntry = (items = [], value) => {
  const merged = mergeControlEffectivenessConfig(items);
  const map = getControlEffectivenessMap(merged);
  const rawValue = normalizeText(value);
  if (!rawValue) {
    return map.unrated;
  }

  const normalizedKey = normalizeControlEffectivenessKey(rawValue, { blankKey: "unrated" });
  if (normalizedKey && map[normalizedKey]) {
    return map[normalizedKey];
  }

  return {
    ...map.unrated,
    label: toTitleCase(rawValue) || map.unrated.label,
    isFallback: true,
    rawValue,
  };
};

export const getControlEffectivenessLabel = (items = [], value) =>
  getControlEffectivenessEntry(items, value).label;

export const buildControlEffectivenessOptions = (items = [], { includeBlank = true } = {}) => {
  const merged = mergeControlEffectivenessConfig(items);
  const actualRatings = merged.filter((item) => item.value !== "unrated");
  const options = actualRatings.map((item) => ({
    value: item.value,
    label: item.label,
  }));

  if (!includeBlank) {
    return options;
  }

  const unrated = merged.find((item) => item.value === "unrated") || DEFAULT_CONTROL_EFFECTIVENESS_CONFIG[3];
  return [{ value: "", label: unrated.label }, ...options];
};

const hexToRgb = (hex) => {
  const normalized = normalizeHexColor(hex, "");
  if (!normalized) return null;
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
};

export const getReadableTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#111827";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.58 ? "#111827" : "#F9FAFB";
};

export const buildControlEffectivenessBadgeToken = (entry) => {
  const color = normalizeHexColor(entry?.color, "#6B7280");
  return {
    label: entry?.label || "Unknown",
    background: color,
    border: color,
    color: getReadableTextColor(color),
    tone: "neutral",
  };
};

export const isValidControlEffectivenessColor = (value) => HEX_COLOR_RE.test(normalizeText(value));
